import { type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Users, ShieldCheck, UserCog } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { fetchConsoleUsers, type ConsoleUserRow } from "@/lib/djayma";

// ---------------------------------------------------------------------------
// DJAYMA access / users directory (Epic 0.3) — the console-scoped roster of
// every actor with a role in the platform: agents, warehouse staff, drivers,
// buyers, suppliers, wholesalers, and back-office managers. Reads flow through
// fn_console_users (migrations 131-140, djayma schema exposure); a JWT without
// the right scope simply sees nothing.
// ---------------------------------------------------------------------------

const ROLE_LABELS: Record<string, string> = {
  super_admin: "Super admin",
  direction: "Direction",
  ops_manager: "Resp. opérations",
  finance: "Finance",
  sales_manager: "Resp. ventes",
  sales_agent: "Agent commercial",
  call_center: "Centre d'appel",
  warehouse_manager: "Resp. entrepôt",
  warehouse_staff: "Magasinier",
  driver: "Chauffeur",
  buyer: "Acheteur",
  supplier: "Fournisseur",
  wholesaler: "Grossiste",
};

function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

function Kpi({
  icon,
  label,
  value,
  tone,
}: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly value: string | number;
  readonly tone: string;
}) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}>{icon}</div>
        <div>
          <p className="text-2xl font-semibold text-stone-900">{value}</p>
          <p className="text-sm text-stone-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

export default function AccessDirectory() {
  const q = useQuery({ queryKey: ["djayma", "console-users"], queryFn: fetchConsoleUsers, retry: false });

  const rows: ConsoleUserRow[] = q.data ?? [];
  const total = rows.length;
  const activeCount = rows.filter((r) => r.is_active).length;
  const inactiveCount = total - activeCount;
  const distinctRoles = new Set(rows.map((r) => r.role)).size;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">Accès &amp; utilisateurs</h1>
            <p className="text-sm text-stone-500">
              Annuaire des rôles et accès — agents, entrepôt, chauffeurs, fournisseurs et managers
            </p>
          </div>
        </div>

        {q.isError && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Annuaire non joignable — vérifiez les migrations 131-140 et l&apos;exposition du schéma
            <code className="mx-1 rounded bg-amber-100 px-1">djayma</code>.
          </div>
        )}

        {/* KPI row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi
            icon={<Users className="h-5 w-5" />}
            tone="bg-stone-100 text-stone-700"
            label="Utilisateurs"
            value={total}
          />
          <Kpi
            icon={<ShieldCheck className="h-5 w-5" />}
            tone="bg-emerald-100 text-emerald-700"
            label="Actifs"
            value={activeCount}
          />
          <Kpi
            icon={<ShieldCheck className="h-5 w-5" />}
            tone="bg-rose-100 text-rose-700"
            label="Inactifs"
            value={inactiveCount}
          />
          <Kpi
            icon={<UserCog className="h-5 w-5" />}
            tone="bg-amber-100 text-amber-700"
            label="Rôles distincts"
            value={distinctRoles}
          />
        </div>

        {/* Directory table */}
        <section className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-stone-700">Annuaire des utilisateurs</h2>
          {rows.length === 0 ? (
            <p className="text-sm text-stone-400">Aucun utilisateur.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-stone-400">
                  <tr>
                    <th className="py-2 pr-4 font-medium">Nom</th>
                    <th className="py-2 pr-4 font-medium">Rôle</th>
                    <th className="py-2 pr-4 font-medium">Téléphone</th>
                    <th className="py-2 pr-4 font-medium">Code agent</th>
                    <th className="py-2 pr-4 font-medium">Rattachement</th>
                    <th className="py-2 pr-4 font-medium">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className="py-2 pr-4 text-stone-700">{r.full_name}</td>
                      <td className="py-2 pr-4 text-stone-500">{roleLabel(r.role)}</td>
                      <td className="py-2 pr-4 text-stone-500">{r.phone ?? "—"}</td>
                      <td className="py-2 pr-4 text-stone-500">{r.agent_code ?? "—"}</td>
                      <td className="py-2 pr-4 text-stone-500">{r.shop_name ?? r.supplier_name ?? "—"}</td>
                      <td className="py-2 pr-4">
                        {r.is_active ? (
                          <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                            Actif
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-500">
                            Inactif
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}
